import { FunctionComponent, useCallback, useState } from 'react'
import { QuestionnaireQueryFilters, SearchScoresProps } from './SearchQuestionnairesSurvey.model'
import createSearchPanel, { FiltersType } from '../../../containers/SearchPanel'
import { LightQuestionnaire } from '../../../model/Questionnaire'
import { Button } from '../../shared'
import { QuestionnaireListItem } from '../atoms'
import { useGetInfiniteQuestionnaireTemplates } from '../../../hooks/queries/questionnaireTemplates'
import { isEqual } from 'lodash'
import { BottomPanel } from '../../shared/bottomPanel/BottomPanel'
import { addScoresToTree, buildSurveyScores } from '../../../misc/survey.utilities'
import { useUpdateSurvey } from '../../../hooks/queries/survey'
import { isVisibilityType, VisibilityType } from '../../../constants'
import {
  useVisibilityOptions,
  VisibilityLabel,
} from '../../../hooks/utils/visibilityFilters/useVisibilityOptions.hook'

const SearchPanel = createSearchPanel<LightQuestionnaire>()

export const SearchQuestionnairesSurvey: FunctionComponent<SearchScoresProps> = ({
  survey,
  isUnmounting,
  branch,
  onClose,
}) => {
  const [selected, setSelected] = useState<ReadonlyArray<LightQuestionnaire>>([])

  const [queryFilters, setQueryFilters] = useState<QuestionnaireQueryFilters>({})
  const {
    query: { isLoading, fetchNextPage },
    questionnaireTemplateList: items,
    paginationState,
    cancelPendingQuery,
  } = useGetInfiniteQuestionnaireTemplates({ filters: queryFilters })
  const { mutate: updateSurvey } = useUpdateSurvey()

  const questionnairesVisibilityOptions = useVisibilityOptions(VisibilityLabel.Questionnaires)

  const handleFiltersChange = useCallback(
    (filters) => {
      const newQueryFilters: QuestionnaireQueryFilters = {
        ...(filters.search && { search: filters.search }),
        ...(filters.tagIds.length && { tagIds: filters.tagIds.join(',') }),
        ...(isVisibilityType(filters.visibility)
          ? filters.visibility !== VisibilityType.ALL && {
              private: filters.visibility === VisibilityType.PUBLIC ? 'false' : 'true',
            }
          : {
              userIds: filters.visibility,
            }),
      }

      if (!isEqual(queryFilters, newQueryFilters)) {
        cancelPendingQuery()
        setQueryFilters(newQueryFilters)
      }
    },
    [cancelPendingQuery, queryFilters],
  )

  const applySelection = (item?: LightQuestionnaire) => {
    const selection: number[] = item
      ? [item.id]
      : items
          .filter((questionnaire) => selected.some(({ id }) => id === questionnaire.id))
          .map((score) => score.id)

    const updatedContent = addScoresToTree(
      buildSurveyScores(selection),
      survey.content,
      branch.uuid,
    )

    updateSurvey({
      surveyId: survey.id,
      survey: { content: updatedContent },
    })

    onClose()
  }

  return (
    <BottomPanel
      testId="survey-questionnaire-add"
      isUnmounting={isUnmounting}
      title="Ajouter un questionnaire"
      onRequestClose={onClose}
      actions={
        selected.length > 0 ? (
          <Button theme="primary" label="Valider" onClick={() => applySelection()} />
        ) : null
      }
    >
      <SearchPanel
        items={items}
        loading={isLoading}
        itemCount={paginationState?.itemCount ?? null}
        pageCount={paginationState?.pageCount ?? null}
        currentPage={paginationState?.currentPage ?? 0}
        onMultiSelectionChange={(selection) => setSelected(selection)}
        onPrimaryAction={applySelection}
        getItems={() => fetchNextPage()}
        onFiltersChange={handleFiltersChange}
        renderItem={(item) => <QuestionnaireListItem item={item} />}
        filterCapabilities={[FiltersType.Title, FiltersType.Visibility, FiltersType.Tags]}
        customVisibilityOptions={questionnairesVisibilityOptions}
        testId="survey-questionnaire"
      />
    </BottomPanel>
  )
}
